import type { AdminReportDetail } from '../../../../api/DigiobcanApi';
import {
  deadlineState,
  type DeadlineState,
} from '../../../../components/DeadlineCell';
import { __, formatDate, getVotesDeclension } from '../../../../utils';
import {
  EMPTY_CELL,
  MeterBar,
  Stack,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

/**
 * Naliehavosť dlaždice.
 *
 * `alarm` je jediná plne vyfarbená plocha na obrazovke a je vyhradená
 * zmeškanej lehote: úradník má z rozbehnutej obrazovky poznať na prvý pohľad,
 * že tomuto podnetu už termín ušiel. Nie je to tlačidlo - dlaždica nemá
 * hover ani kurzor a stojí v rade s ostatnými číslami.
 */
type TileTone = 'plain' | 'warning' | 'alarm';

const TILE_TONE: Record<
  TileTone,
  { border: string; background: string; label: string; value: string }
> = {
  plain: {
    border: 'divider',
    background: 'background.default',
    label: 'text.secondary',
    value: 'text.primary',
  },
  // Žltá dlaždica má rám z `warning.light`, nie zo `surface` - ten je takmer
  // biely a na žltom podklade nebolo vidieť, že dlaždica rám má, hoci Hlasy
  // vedľa nej ho majú.
  warning: {
    border: 'warning.light',
    background: 'warning.surface',
    label: 'warning.extraDark',
    value: 'text.primary',
  },
  alarm: {
    border: 'error.main',
    background: 'error.main',
    label: 'common.white',
    value: 'common.white',
  },
};

/**
 * Dlaždica s jedným číslom - Lehota a Hlasy majú rovnaký tvar.
 *
 * `dense` je podoba do hlavičky stránky: menovka a hodnota vedľa seba na
 * jednom riadku, bez pásu. V hlavičke stojí dlaždica v rade s tlačidlami
 * a dvojriadková by z nej urobila najvyšší prvok celej hlavičky.
 */
const Tile: React.FC<{
  /**
   * Menovka nad hodnotou. Nepovinná: Hlasy ju nemajú, lebo ju nesie samotná
   * hodnota („0 hlasov") a nad ňou by to bolo dvakrát to isté slovo.
   */
  label?: string;
  value: React.ReactNode;
  hint?: React.ReactNode;
  tone?: TileTone;
  /** Pás pod hodnotou - koľko z lehoty ubehlo. Hlasy ho nemajú. */
  meter?: React.ReactNode;
  dense?: boolean;
}> = ({ label, value, hint, tone = 'plain', meter, dense = false }) => {
  const colors = TILE_TONE[tone];

  const labelNode = label ? (
    <Typography
      variant='overline'
      color={colors.label}
      noWrap
      sx={{ fontWeight: 700, letterSpacing: '.05em', lineHeight: 1.4 }}
    >
      {label}
    </Typography>
  ) : null;

  // Hodnota a popis na jednom riadku, nie pod sebou: dlaždica má byť prehľad,
  // nie tretina stĺpca.
  const valueNode = (
    <Stack
      direction='row'
      alignItems='baseline'
      gap={0.75}
      sx={{ minWidth: 0 }}
    >
      <Typography
        variant={dense ? 'body2' : 'subtitle1'}
        fontWeight={700}
        color={colors.value}
        noWrap
      >
        {value}
      </Typography>
      {hint && (
        <Typography
          variant='caption'
          color={colors.label}
          noWrap
          sx={{ minWidth: 0 }}
        >
          {hint}
        </Typography>
      )}
    </Stack>
  );

  if (dense) {
    return (
      <Stack
        direction='row'
        alignItems='center'
        gap={0.875}
        sx={{
          flex: 'none',
          px: 1.25,
          py: 0.5,
          borderRadius: 1,
          border: '1px solid',
          borderColor: colors.border,
          backgroundColor: colors.background,
        }}
      >
        {labelNode}
        {valueNode}
      </Stack>
    );
  }

  return (
    <Stack
      sx={{
        flex: 1,
        minWidth: 0,
        px: 1.25,
        py: 0.875,
        borderRadius: 1,
        gap: meter ? 0.625 : 0,
        border: '1px solid',
        borderColor: colors.border,
        backgroundColor: colors.background,
      }}
    >
      {labelNode}
      {valueNode}
      {meter}
    </Stack>
  );
};

const dayWord = (n: number) => {
  if (n === 1) return __('deň');
  if (n < 5) return __('dni');
  return __('dní');
};

/**
 * Hodnota a popis v dlaždici Lehota. Dni sa berú z toho istého výpočtu ako pás
 * a farba - `deadlineState` ich už raz spočítal a druhé rátanie by sa s ním
 * na prelome dňa rozišlo.
 *
 * Po lehote nesie číslo **hodnota**, nie popis: „Po lehote" povie len to, čo
 * už kričí farba dlaždice, kým „24 dní" je jediné, čo úradník z tej dlaždice
 * naozaj potrebuje - o koľko sa mešká. Do popisu preto ide dátum, odkedy sa
 * mešká, nie dokedy sa malo stihnúť.
 */
const deadlineTile = (
  report: AdminReportDetail,
  deadline?: DeadlineState,
): { value: string; hint?: string } => {
  if (!report.deadline_at || !deadline) return { value: EMPTY_CELL };

  const since = `${__('po lehote od')} ${formatDate(deadline.deadline)}`;
  const until = `${__('do')} ${formatDate(deadline.deadline)}`;

  if (deadline.overdue) {
    const late = -deadline.days;
    return { value: `${late} ${dayWord(late)}`, hint: since };
  }
  if (deadline.resolved) return { value: __('Nesleduje sa'), hint: until };
  if (deadline.days === 0) return { value: __('Dnes'), hint: until };
  return { value: `${deadline.days} ${dayWord(deadline.days)}`, hint: until };
};

/**
 * Lehota a Hlasy - dve čísla, ktoré platia pre podnet ako celok.
 *
 * Na desktope stoja v **hlavičke stránky** (`dense`), lebo sa netýkajú ničoho
 * z toho, čo je pod ňou zvlášť: ani obálky, ani jedného nálezu. Na mobile,
 * kde hlavička nesie len titul, zostávajú v obálke.
 */
const ReportTiles: React.FC<{
  report: AdminReportDetail;
  dense?: boolean;
}> = ({ report, dense = false }) => {
  const deadline = React.useMemo(
    () =>
      deadlineState({
        submittedAt: report.submitted_at,
        deadlineAt: report.deadline_at,
        overdue: report.overdue,
        issues: report.issues,
      }),
    [report.submitted_at, report.deadline_at, report.overdue, report.issues],
  );

  return (
    <Stack direction='row' gap={1} sx={dense ? { flex: 'none' } : undefined}>
      {/* Lehota nesie ten istý výpočet ako stĺpec Lehota v zozname
          ([[DeadlineCell]]) - vrátane pásu. Úradník tie dve obrazovky vidí
          za sebou a rozdiel v jednom dni by znamenal, že jednej neverí. */}
      <Tile
        dense={dense}
        label={__('Lehota')}
        {...deadlineTile(report, deadline)}
        tone={
          deadline?.overdue ? 'alarm' : deadline?.soon ? 'warning' : 'plain'
        }
        meter={
          !dense && deadline?.pct !== undefined ? (
            <MeterBar
              value={deadline.pct}
              label={deadline.fullRel}
              color={deadline.overdue ? 'common.white' : deadline.color}
              trackColor={
                deadline.overdue ? 'rgba(255, 255, 255, 0.35)' : undefined
              }
            />
          ) : undefined
        }
      />
      {/* Bez menovky - „HLASY 0 hlasov" hovorilo to isté dvakrát. */}
      <Tile
        dense={dense}
        value={report.votes_count ?? 0}
        hint={getVotesDeclension(report.votes_count ?? 0)}
      />
    </Stack>
  );
};

export default ReportTiles;
